import React, { useState, useRef, useEffect } from 'react';
import { MousePointer2, Hand, StickyNote, LayoutGrid, Square, Type, Shapes, RectangleHorizontal, Circle, Minus, Triangle, InspectionPanel, GitBranch, Workflow, Route, ArrowRight, Diamond } from 'lucide-react';
import { CreateMode, ShapeType } from '../editing/types';

interface ModernToolbarProps {
    currentMode: CreateMode;
    onModeChange: (mode: CreateMode) => void;
    onAutoCreate?: (mode: CreateMode) => void;
    onConnectorModeChange?: (mode: 'straight' | 'bent' | 'curved' | null) => void;
    connectorMode?: 'straight' | 'bent' | 'curved' | null;
}

const shapeOptions: Array<{ id: ShapeType; icon: any; label: string; rotation?: number }> = [
    { id: 'rectangle-rounded', icon: Square, label: 'Rounded Square' },
    { id: 'rectangle-pill', icon: RectangleHorizontal, label: 'Pill' },
    { id: 'circle', icon: Circle, label: 'Circle' },
    { id: 'triangle-up', icon: Triangle, label: 'Triangle Up' },
    { id: 'triangle-down', icon: Triangle, label: 'Triangle Down', rotation: 180 },
    { id: 'diamond', icon: Diamond, label: 'Diamond' },
    { id: 'line-curved-arrow', icon: ArrowRight, label: 'Curved Arrow' },
    { id: 'line-parallel', icon: Minus, label: 'Parallel Lines' },
];

const connectorOptions: Array<{ id: 'straight' | 'bent' | 'curved'; icon: any; label: string }> = [
    { id: 'straight', icon: Minus, label: 'Straight Connector (L)' },
    { id: 'bent', icon: Route, label: 'Bent Connector (X)' },
    { id: 'curved', icon: GitBranch, label: 'Curved Connector' },
];

const toolbarItems = [
    { id: 'select', icon: MousePointer2, label: 'Select', mode: { type: 'select' as const } },
    { id: 'pan', icon: Hand, label: 'Pan', mode: { type: 'pan' as const }, hasDivider: true },
    { id: 'sticky', icon: StickyNote, label: 'Sticky Note', mode: { type: 'sticky' as const } },
    { id: 'organizer', icon: LayoutGrid, label: 'Organizer', mode: { type: 'organizer' as const } },
    { id: 'section', icon: InspectionPanel, label: 'Section', mode: { type: 'section' as const } },
    { id: 'text', icon: Type, label: 'Text', mode: { type: 'text' as const } },
    { id: 'shapes', icon: Shapes, label: 'Shapes', mode: { type: 'shape' as const, shapeType: 'rectangle-rounded' as const }, isShapeMenu: true,  hasDivider: true},
    { id: 'connector', icon: Workflow, label: 'Connectors', isConnectorMenu: true,  },
];

export const ModernToolbar: React.FC<ModernToolbarProps> = ({ 
    currentMode, 
    onModeChange, 
    onAutoCreate,
    onConnectorModeChange,
    connectorMode = null,
}) => {
    const [showShapesMenu, setShowShapesMenu] = useState(false);
    const [showConnectorMenu, setShowConnectorMenu] = useState(false);
    const [isToolbarVisible, setIsToolbarVisible] = useState(false);
    const [isShapesMenuVisible, setIsShapesMenuVisible] = useState(false);
    const [isConnectorMenuVisible, setIsConnectorMenuVisible] = useState(false);
    const shapesButtonRef = useRef<HTMLButtonElement>(null);
    const shapesMenuRef = useRef<HTMLDivElement>(null);
    const connectorButtonRef = useRef<HTMLButtonElement>(null);
    const connectorMenuRef = useRef<HTMLDivElement>(null);

    // Trigger toolbar animation on mount
    useEffect(() => {
        setIsToolbarVisible(true);
    }, []);

    // Trigger menu animations when they appear
    useEffect(() => {
        if (showShapesMenu) {
            setIsShapesMenuVisible(true);
        } else {
            setIsShapesMenuVisible(false);
        }
    }, [showShapesMenu]);

    useEffect(() => {
        if (showConnectorMenu) {
            setIsConnectorMenuVisible(true);
        } else {
            setIsConnectorMenuVisible(false);
        }
    }, [showConnectorMenu]);

    // Close shapes menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                shapesMenuRef.current &&
                !shapesMenuRef.current.contains(event.target as Node) &&
                shapesButtonRef.current &&
                !shapesButtonRef.current.contains(event.target as Node)
            ) {
                setShowShapesMenu(false);
            }
            if (
                connectorMenuRef.current &&
                !connectorMenuRef.current.contains(event.target as Node) &&
                connectorButtonRef.current &&
                !connectorButtonRef.current.contains(event.target as Node)
            ) {
                setShowConnectorMenu(false);
            }
        };

        if (showShapesMenu || showConnectorMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
        return undefined;
    }, [showShapesMenu, showConnectorMenu]);

    const isActive = (item: typeof toolbarItems[0]): boolean => {
        if (item.isShapeMenu) {
            return currentMode.type === 'shape';
        }
        if (item.isConnectorMenu) {
            return connectorMode !== null;
        }
        if (!item.mode || item.mode.type !== currentMode.type) return false;
        if (item.mode.type === 'shape' && currentMode.type === 'shape') {
            return item.mode.shapeType === currentMode.shapeType;
        }
        return true;
    };

    const handleShapeSelect = (shapeType: ShapeType) => {
        const mode: CreateMode = { type: 'shape', shapeType };
        // Always use onModeChange to show preview, not onAutoCreate
        onModeChange(mode);
        setShowShapesMenu(false);
    };

    const handleConnectorSelect = (connectorType: 'straight' | 'bent' | 'curved') => {
        if (onConnectorModeChange) {
            // Toggle off if already active, otherwise set to selected type
            onConnectorModeChange(connectorMode === connectorType ? null : connectorType);
        }
        setShowConnectorMenu(false);
    };

    const getCurrentConnectorIcon = () => {
        if (connectorMode) {
            const connector = connectorOptions.find(c => c.id === connectorMode);
            return connector?.icon || Workflow;
        }
        return Workflow;
    };

    const getCurrentShapeIcon = () => {
        if (currentMode.type === 'shape') {
            const shape = shapeOptions.find(s => s.id === currentMode.shapeType);
            return shape?.icon || Shapes;
        }
        return Shapes;
    };

    return (
        <>
            {/* Connector popup menu */}
            {showConnectorMenu && (
                <div
                    ref={connectorMenuRef}
                    style={{
                        position: 'absolute',
                        bottom: '80px',
                        left: '50%',
                        zIndex: 1001,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '2px',
                        padding: '6px 16px',
                        backgroundColor: 'white',
                        borderRadius: '9999px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgb(229, 231, 235)',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        opacity: isConnectorMenuVisible ? 1 : 0,
                        transform: isConnectorMenuVisible ? 'translateX(50%) translateY(0)' : 'translateX(50%) translateY(20px)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    {connectorOptions.map((connector) => {
                        const Icon = connector.icon;
                        const isConnectorActive = connectorMode === connector.id;

                        return (
                            <button
                                key={connector.id}
                                onClick={() => handleConnectorSelect(connector.id)}
                                title={connector.label}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    border: 'none',
                                    borderRadius: '6px ',
                                    backgroundColor: isConnectorActive ? 'rgba(0, 134, 201, 0.1)' : 'transparent',
                                    color: isConnectorActive ? '#0086C9' : 'rgb(107, 114, 128)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isConnectorActive) {
                                        e.currentTarget.style.backgroundColor = 'rgb(249, 250, 251)';
                                        e.currentTarget.style.color = 'rgb(17, 24, 39)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isConnectorActive) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = 'rgb(107, 114, 128)';
                                    }
                                }}
                            >
                                <Icon size={20} strokeWidth={isConnectorActive ? 2 : 1.5} />
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Shapes popup menu */}
            {showShapesMenu && (
                <div
                    ref={shapesMenuRef}
                    style={{
                        position: 'absolute',
                        bottom: '80px',
                        left: '50%',
                        zIndex: 1001,
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '2px',
                        padding: '6px',
                        backgroundColor: 'white',
                        borderRadius: '9999px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgb(229, 231, 235)',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        opacity: isShapesMenuVisible ? 1 : 0,
                        transform: isShapesMenuVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(20px)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    {shapeOptions.map((shape) => {
                        const Icon = shape.icon;
                        const isShapeActive = currentMode.type === 'shape' && currentMode.shapeType === shape.id;

                        return (
                            <button
                                key={shape.id}
                                onClick={() => handleShapeSelect(shape.id)}
                                title={shape.label}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    border: 'none',
                                    borderRadius: '6px 16px',
                                    backgroundColor: isShapeActive ? 'rgba(0, 134, 201, 0.1)' : 'transparent',
                                    color: isShapeActive ? '#0086C9' : 'rgb(107, 114, 128)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: shape.rotation ? `rotate(${shape.rotation}deg)` : undefined,
                                }}
                                onMouseEnter={(e) => {
                                    if (!isShapeActive) {
                                        e.currentTarget.style.backgroundColor = 'rgb(249, 250, 251)';
                                        e.currentTarget.style.color = 'rgb(17, 24, 39)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isShapeActive) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = 'rgb(107, 114, 128)';
                                    }
                                }}
                            >
                                <Icon size={20} strokeWidth={isShapeActive ? 2 : 1.5} />
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Main toolbar */}
            <div
                style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: isToolbarVisible ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(20px)',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '2px',
                    padding: '6px 16px',
                    backgroundColor: 'white',
                    borderRadius: '9999px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgb(229, 231, 235)',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    opacity: isToolbarVisible ? 1 : 0,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
            >
                {toolbarItems.map((item) => {
                    const active = isActive(item);
                    const Icon = item.isShapeMenu 
                        ? getCurrentShapeIcon() 
                        : item.isConnectorMenu 
                        ? getCurrentConnectorIcon() 
                        : item.icon;

                    return (
                        <React.Fragment key={item.id}>
                            <button
                                ref={item.isShapeMenu ? shapesButtonRef : item.isConnectorMenu ? connectorButtonRef : undefined}
                                onClick={() => {
                                    if (item.isShapeMenu) {
                                        setShowShapesMenu(!showShapesMenu);
                                        setShowConnectorMenu(false);
                                    } else if (item.isConnectorMenu) {
                                        setShowConnectorMenu(!showConnectorMenu);
                                        setShowShapesMenu(false);
                                    } else if (item.mode) {
                                        // Always use onModeChange to show preview, not onAutoCreate
                                        onModeChange(item.mode);
                                        if (onConnectorModeChange) {
                                            onConnectorModeChange(null);
                                        }
                                    }
                                }}
                                title={item.label}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    backgroundColor: active ? 'rgba(0, 134, 201, 0.1)' : 'transparent',
                                    color: active ? '#0086C9' : 'rgb(107, 114, 128)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                                    position: 'relative',
                                }}
                                onMouseEnter={(e) => {
                                    if (!active) {
                                        e.currentTarget.style.backgroundColor = 'rgb(249, 250, 251)';
                                        e.currentTarget.style.color = 'rgb(17, 24, 39)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!active) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = 'rgb(107, 114, 128)';
                                    }
                                }}
                            >
                                <Icon size={20} strokeWidth={active ? 2 : 1.5} />
                                {active && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: '-8px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '20px',
                                            height: '3px',
                                            backgroundColor: '#0086C9',
                                            borderRadius: '2px 2px 0 0',
                                        }}
                                    />
                                )}
                            </button>
                            {item.hasDivider && (
                                <div
                                    style={{
                                        width: '1px',
                                        height: '24px',
                                        backgroundColor: 'rgb(229, 231, 235)',
                                        margin: '6px 4px',
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </>
    );
};
